import { API_CONFIG, ERROR_MESSAGES } from '@/constants'
import { ApiResponse, HttpClient, RequestConfig } from '../types'

/**
 * Internal result of a single HTTP attempt. `status` is the raw HTTP status (null
 * when the request never reached the server, e.g. network failure or timeout) and
 * is what the retry loop reasons about - the user-facing `error` string must never
 * be pattern-matched for status codes.
 */
interface AttemptResult<T> {
  result: ApiResponse<T>
  status: number | null
}

/**
 * An error carrying the *parsed* body of a failed response.
 *
 * `makeRequest` signals failure by throwing, and the catch block builds the
 * `ApiResponse`. Without this the structured body would be readable only inside
 * the try block and would never reach the caller — which is exactly what used to
 * strip `missing_fields` off a 400 from `POST /attestations/{id}/submit`.
 */
class HttpError extends Error {
  readonly body: unknown

  constructor(message: string, body: unknown) {
    super(message)
    this.name = 'HttpError'
    this.body = body
  }
}

class ApiClient implements HttpClient {
  private readonly baseURL: string
  private readonly timeout: number
  private readonly retryAttempts: number
  private tokenGetter: () => string | null
  private unauthorizedCount = 0
  private maxUnauthorizedAttempts = 3

  constructor(tokenGetter: () => string | null = () => localStorage.getItem('token')) {
    this.baseURL = API_CONFIG.BASE_URL
    this.timeout = API_CONFIG.TIMEOUT
    this.retryAttempts = API_CONFIG.RETRY_ATTEMPTS
    this.tokenGetter = tokenGetter
  }

  protected getAuthToken(): string | null {
    return this.tokenGetter()
  }

  private handleUnauthorized(): void {
    this.unauthorizedCount++
    
    // Only logout after multiple failed attempts
    if (this.unauthorizedCount >= this.maxUnauthorizedAttempts) {
      this.logoutUser()
    }
  }

  private logoutUser(): void {
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    this.unauthorizedCount = 0
    
    // Redirect to login page
    if (typeof window !== 'undefined') {
      window.location.href = '/login'
    }
  }

  private resetUnauthorizedCount(): void {
    if (this.unauthorizedCount > 0) {
      this.unauthorizedCount = 0
    }
  }

  private getDefaultHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    const token = this.getAuthToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    return headers
  }

  private async makeRequest<T>(config: RequestConfig): Promise<AttemptResult<T>> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.timeout)

    let fullUrl = `${this.baseURL}${config.url}`
    
    // Add query parameters if provided
    if (config.params) {
      const searchParams = new URLSearchParams()
      Object.entries(config.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      })
      const queryString = searchParams.toString()
      if (queryString) {
        fullUrl += `?${queryString}`
      }
    }


    let status: number | null = null

    try {
      const response = await fetch(fullUrl, {
        method: config.method,
        headers: {
          ...this.getDefaultHeaders(),
          ...config.headers,
        },
        body: config.data ? JSON.stringify(config.data) : undefined,
        signal: controller.signal,
      })

      clearTimeout(timeoutId)
      status = response.status

      if (!response.ok) {
        // Read the body once and keep it: the server's message ("cannot remove
        // last owner", "ADMIN cannot invite an OWNER", ...) is far more useful
        // than the generic status-code string. The parsed body travels alongside
        // it so callers can branch on structure (`missing_fields`, ...) rather
        // than on prose.
        const { message: serverMessage, body } = await this.extractServerError(response)

        if (process.env.NODE_ENV === 'development') {
          console.error(`API Error ${response.status}:`, serverMessage ?? '(no readable body)')
        }

        // Handle 401 Unauthorized with smart logic.
        //
        // `skipAuthLogout` requests opt out entirely (see RequestConfig): the
        // counter is shared by every caller of this singleton, so a background
        // refresh that 401s three times would log the user out of the app while
        // they are still working. Those callers surface the failure themselves.
        if (response.status === 401 && !config.skipAuthLogout) {
          this.handleUnauthorized()
        }

        throw new HttpError(serverMessage || this.getErrorMessage(response.status), body)
      }

      // Reset unauthorized count on successful request
      this.resetUnauthorizedCount()

      const result = await response.json()
      return { result: { success: true, data: result, status }, status }
    } catch (error) {
      clearTimeout(timeoutId)

      if (process.env.NODE_ENV === 'development') {
      console.error('Request failed:', error)
      }
      if (error instanceof Error) {
        return {
          result: {
            success: false,
            error: error.message === 'The operation was aborted.'
              ? 'Request timeout'
              : error.message,
            status: status ?? undefined,
            errorBody: error instanceof HttpError ? error.body : undefined,
          },
          status,
        }
      }

      return {
        result: { success: false, error: ERROR_MESSAGES.SERVER_ERROR, status: status ?? undefined },
        status,
      }
    }
  }

  /**
   * Pull both halves of an error response out of the body in one read.
   *
   * `message` is human-readable prose, or null when the body is empty,
   * unreadable, or not worth showing a user (HTML error pages, huge blobs) - the
   * caller then falls back to the generic ERROR_MESSAGES string for the status.
   * `body` is the parsed JSON, present only when the body actually was JSON.
   *
   * A response body can only be read once, so these cannot be two methods.
   */
  private async extractServerError(
    response: Response,
  ): Promise<{ message: string | null; body: unknown }> {
    let raw: string
    try {
      raw = await response.text()
    } catch {
      return { message: null, body: undefined }
    }

    const trimmed = raw.trim()
    if (!trimmed) return { message: null, body: undefined }

    let parsed: unknown = null
    let isJson = true
    try {
      parsed = JSON.parse(trimmed)
    } catch {
      isJson = false
    }

    // A JSON body we can't find a message in is structured data, not prose -
    // never dump it at the user; fall back to the generic message instead. The
    // parsed body still goes back, because a caller may understand a shape the
    // message extractor cannot.
    if (isJson) return { message: this.pickMessage(parsed), body: parsed }

    // Plain-text errors are usable; HTML error pages and oversized blobs are not.
    if (trimmed.startsWith('<') || trimmed.length > 300) {
      return { message: null, body: undefined }
    }
    return { message: trimmed, body: undefined }
  }

  /** Walk the common error envelopes (FastAPI `detail`, `message`, `error`, ...). */
  private pickMessage(payload: unknown, depth = 0): string | null {
    if (typeof payload === 'string') return payload.trim() || null
    if (depth > 3 || !payload || typeof payload !== 'object') return null

    const candidates = [
      (payload as Record<string, unknown>).detail,
      (payload as Record<string, unknown>).message,
      (payload as Record<string, unknown>).error,
      (payload as Record<string, unknown>).error_description,
      (payload as Record<string, unknown>).msg,
      (payload as Record<string, unknown>).data,
    ]

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) return candidate.trim()

      if (Array.isArray(candidate)) {
        // FastAPI validation errors: [{ loc, msg, type }, ...]
        const parts = candidate
          .map(item => this.pickMessage(item, depth + 1))
          .filter((part): part is string => !!part)
        if (parts.length) return parts.join('; ')
        continue
      }

      if (candidate && typeof candidate === 'object') {
        const nested = this.pickMessage(candidate, depth + 1)
        if (nested) return nested
      }
    }

    return null
  }

  private getErrorMessage(status: number): string {
    switch (status) {
      case 401:
        return ERROR_MESSAGES.UNAUTHORIZED
      case 403:
        return ERROR_MESSAGES.FORBIDDEN
      case 404:
        return ERROR_MESSAGES.NOT_FOUND
      case 422:
        return ERROR_MESSAGES.VALIDATION_ERROR
      case 500:
        return ERROR_MESSAGES.SERVER_ERROR
      default:
        return ERROR_MESSAGES.NETWORK_ERROR
    }
  }

  async request<T>(config: RequestConfig): Promise<ApiResponse<T>> {
    let lastError: ApiResponse<T> | null = null

    for (let attempt = 0; attempt <= this.retryAttempts; attempt++) {
      try {
        const { result, status } = await this.makeRequest<T>(config)

        if (result.success) {
          return result
        }

        lastError = result

        // Don't retry client errors. This used to test the already-humanized
        // message for "401"/"403", which never matched, so auth failures were
        // retried 3x. Client errors are deterministic - a retry cannot change
        // the outcome - except for 408 (timeout) and 429 (rate limited).
        const isNonRetryableClientError =
          status !== null && status >= 400 && status < 500 && status !== 408 && status !== 429
        if (isNonRetryableClientError) {
          break
        }

        // Exponential backoff for retries
        if (attempt < this.retryAttempts) {
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000))
        }
      } catch {
        lastError = { success: false, error: ERROR_MESSAGES.SERVER_ERROR }
      }
    }

    return lastError || { success: false, error: ERROR_MESSAGES.SERVER_ERROR }
  }

  async get<T>(url: string, config?: { headers?: Record<string, string>, params?: Record<string, any> }): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'GET', url, headers: config?.headers, params: config?.params })
  }

  async post<T>(url: string, data?: unknown, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'POST', url, data, headers })
  }

  async put<T>(url: string, data?: unknown, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'PUT', url, data, headers })
  }

  async patch<T>(url: string, data?: unknown, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'PATCH', url, data, headers })
  }

  async delete<T>(url: string, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'DELETE', url, headers })
  }
}

// Singleton instance
export const apiClient = new ApiClient()

// Service layer abstractions
export abstract class BaseService {
  protected readonly api = apiClient

  protected getAuthToken(): string | null {
    return localStorage.getItem('token')
  }

  protected handleError(error: unknown): never {
    if (error instanceof Error) {
      throw error
    }
    throw new Error(ERROR_MESSAGES.SERVER_ERROR)
  }

  protected async get<T>(endpoint: string, options?: { params?: Record<string, any> }): Promise<T> {
    let url = endpoint
    
    // Add query parameters if provided
    if (options?.params) {
      const searchParams = new URLSearchParams()
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value))
        }
      })
      const queryString = searchParams.toString()
      if (queryString) {
        url += `?${queryString}`
      }
    }
    
    return this.request<T>(url, { method: 'GET' })
  }

  protected async post<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  protected async put<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  protected async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' })
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = this.getAuthToken()
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    }

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          ...headers,
          ...options.headers
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const error = new Error(errorData.message || `HTTP error! status: ${response.status}`) as any
        error.status = response.status
        // Don't log 404 errors as they're often handled gracefully by services
        if (response.status !== 404) {
          console.error('API request failed:', error)
        }
        throw error
      }

      // Check if response body is already consumed
      if (response.bodyUsed) {
        throw new Error('Response body already consumed');
      }
      
      const jsonData = await response.json() as T;
      return jsonData;
    } catch (error: any) {
      // Only log if it's not a 404 (404s are often handled gracefully)
      if (error?.status !== 404) {
        console.error(`[BaseService.request] Error for ${endpoint}:`, error)
      }
      throw error
    }
  }
} 